import type { KitchenSheet, TrayClass } from '@bakery/shared';
import { escapeHtml, trayRule } from '@bakery/shared';

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/**
 * The sheet that goes up on the kitchen bench.
 *
 * Built for a floury room rather than a screen: one A4 page, set larger than a
 * desk document so it reads at arm's length, with a box to tick as each tray
 * goes in. The copy that comes back marked up is the record of what was baked.
 *
 * What goes in the oven, in the order of the morning, and then what has to be
 * got off the shelf to go with it — a dipping set, a spread, a bottle of oil.
 * Who each loaf is for is deliberately absent, and so is boxing the pastries
 * up afterwards: that is done from the receipts, and a reminder about it here
 * was a section the baker read past.
 *
 * What a customer asked for is on the tray line itself, because for some goods
 * it is not a remark but the specification — a tray of twelve minis tells the
 * baker nothing until it says which minis.
 */
export function buildKitchenSheetHtml(sheet: KitchenSheet, heading: string): string {
  /*
   * Bread and focaccia: what to bake and how much, nothing about trays. The
   * quantity leads the line because that is the number being read.
   */
  const listBlock = (list: KitchenSheet['lists'][number]) => `
      <tr class="band">
        <th colspan="3">${escapeHtml(list.label)}</th>
        <th class="num">${list.total}</th>
      </tr>
      ${list.lines
        .map(
          (line) => `
          <tr>
            <td class="tick"></td>
            <td colspan="3">
              <span class="item${line.note ? ' asked' : ''}">
                <span class="good"><b>${line.qty}</b> ${escapeHtml(line.name)}${
                  line.size ? ` <span class="size">${escapeHtml(line.size)}</span>` : ''
                }</span>
                ${line.note ? `<span class="ask">&rarr; ${escapeHtml(line.note)}</span>` : ''}
              </span>
            </td>
          </tr>`,
        )
        .join('')}`;

  const trayBlock = (trayClass: TrayClass) => {
    const trays = sheet.trays.filter((tray) => tray.trayClass === trayClass);
    if (trays.length === 0) return '';
    const { label, rule } = trayRule(trayClass);

    const rows = trays
      .map(
        (tray) => `
          <tr>
            <td class="tick"></td>
            <td class="tray">Tray ${tray.index}</td>
            <td>${tray.lines
              .map(
                (line) =>
                  `<span class="item${line.note ? ' asked' : ''}"><span class="good"><b>${line.qty}</b> ${escapeHtml(
                    line.name,
                  )}${line.size ? ` <span class="size">${escapeHtml(line.size)}</span>` : ''}</span>${
                    line.note ? `<span class="ask">&rarr; ${escapeHtml(line.note)}</span>` : ''
                  }</span>`,
              )
              .join('')}</td>
            <td class="num">${tray.isPartial ? '<span class="part">part</span>' : ''}</td>
          </tr>`,
      )
      .join('');

    return `
      <tr class="band">
        <th colspan="3">${escapeHtml(label)} <span class="rule">${escapeHtml(rule)}</span></th>
        <th class="num">${plural(trays.length, 'tray', 'trays')}</th>
      </tr>
      ${rows}`;
  };


  const counts = [
    sheet.totals.focaccia > 0 ? `${sheet.totals.focaccia} focaccia` : '',
    sheet.totals.bread > 0 ? `${sheet.totals.bread} bread` : '',
    sheet.totals.pastry > 0 ? `${sheet.totals.pastry} pastries` : '',
    plural(sheet.trays.length, 'tray', 'trays'),
  ]
    .filter(Boolean)
    .join(' &middot; ');

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Kitchen sheet &mdash; ${escapeHtml(heading)}</title>
    <style>
      @page { size: A4 portrait; margin: 11mm; }
      * { box-sizing: border-box; }
      body { font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; color: #111; margin: 0; }
      h1 { font-size: 17pt; margin: 0; }
      h2 { font-size: 10pt; text-transform: uppercase; letter-spacing: 0.08em; margin: 7mm 0 2mm; color: #444; }
      .sub { font-size: 10pt; color: #555; margin: 1.5mm 0 4mm; }
      table { width: 100%; border-collapse: collapse; }
      /* Larger than a desk document: this is read across a bench. */
      .trays { font-size: 11pt; }
      .trays td, .trays th { border-bottom: 0.4pt solid #ccc; padding: 2mm 1.4mm; text-align: left; vertical-align: middle; }
      .band th { background: #eee; border-top: 0.8pt solid #333; font-size: 10.5pt; padding: 2mm 1.4mm; }
      .rule { font-weight: 400; color: #555; font-size: 9pt; }
      .tray { white-space: nowrap; font-weight: 600; width: 20mm; }
      /* One good per line: a name that wraps mid-word is a name misread. */
      .item { display: block; }
      .item + .item { margin-top: 1.4mm; }
      .good { display: block; }
      .good b { font-size: 12pt; }
      .size { font-size: 8pt; color: #666; border: 0.4pt solid #bbb; border-radius: 2pt; padding: 0 1mm; }
      .num { text-align: right; white-space: nowrap; font-size: 9pt; color: #555; }
      .part { color: #a33; }
      /* Ticked as the tray goes in; the copy that comes back is the record. */
      .tick { width: 8mm; }
      .tick::after { content: ''; display: block; width: 5mm; height: 5mm; border: 0.7pt solid #333; }
      tr { page-break-inside: avoid; }
      .order { border-top: 0.4pt solid #ccc; padding: 2mm 0; page-break-inside: avoid; }
      .who { font-size: 10pt; margin-bottom: 1mm; }
      .meta { color: #666; font-size: 9pt; margin-left: 2mm; }
      .ahead { font-size: 8pt; border: 0.4pt solid #a33; color: #a33; border-radius: 2pt; padding: 0 1mm; margin-left: 2mm; }
      .items { font-size: 10pt; }
      .items td { padding: 0.8mm 1.4mm; border: 0; vertical-align: top; }
      .qty { width: 10mm; text-align: right; font-weight: 600; }
      /* A line somebody asked something about, which is the point of the page. */
      .flagged td { background: #fdf3d8; }
      /* What was asked for, on its own line under the good it belongs to. */
      .ask { display: block; color: #7a4a00; font-weight: 600; padding-left: 4mm; }
      /* A tray line somebody asked something about, which is the point of the page. */
      .item.asked { background: #fdf3d8; padding: 0.8mm 1.2mm; border-radius: 2pt; }
      .arrow { color: #a37200; font-weight: 700; }
      .ordernote { font-size: 9.5pt; color: #7a4a00; background: #fdf3d8; margin: 1mm 0 0; padding: 1mm 1.4mm; }
      .plain { font-size: 10pt; }
      .plain td { padding: 1.2mm 1.4mm; border-bottom: 0.4pt solid #ddd; }
      .note { color: #555; font-size: 8.5pt; margin-top: 5mm; line-height: 1.5; }
    </style>
  </head>
  <body>
    <h1>Kitchen sheet</h1>
    <p class="sub">${escapeHtml(heading)} &middot; ${counts}</p>

    ${
      sheet.lists.length > 0 || sheet.trays.length > 0
        ? `<table class="trays"><tbody>
            ${sheet.lists.map(listBlock).join('')}
            ${trayBlock('pastry')}
          </tbody></table>`
        : '<p class="sub">Nothing to bake on this day.</p>'
    }

    ${
      sheet.fromTheShelf.length > 0
        ? `<h2>Not baked &mdash; take from the shelf</h2>
           <table class="trays"><tbody>
             ${sheet.fromTheShelf
               .map(
                 (line) => `
                 <tr>
                   <td class="tick"></td>
                   <td colspan="3"><span class="item"><span class="good"><b>${line.qty}</b> ${escapeHtml(
                     line.name,
                   )}</span></span></td>
                 </tr>`,
               )
               .join('')}
           </tbody></table>`
        : ''
    }

    ${
      sheet.alsoAsked.length > 0
        ? `<h2>Also asked for</h2>
           <table class="plain"><tbody>
             ${sheet.alsoAsked
               .map(
                 (line) =>
                   `<tr><td class="qty">${line.qty}</td><td>${escapeHtml(line.name)}</td>
                    <td class="ask">&rarr; ${escapeHtml(line.note)}</td></tr>`,
               )
               .join('')}
           </tbody></table>
           <p class="note">Nothing above is being baked today — the shelf already covers it.</p>`
        : ''
    }

  </body>
</html>`;
}
